import { useState } from 'react';
import type { ReactNode } from 'react';
import { useActions, useLive, useRuntime } from './context';
import { formatMoney } from '../mirror/time';
import { centsFromInput, inputFromCents } from '../money/input';
import type { Product } from '../wire';
import { ItemPhotos, ItemThumb } from './ItemPhotos';
import { canChangeCatalog } from './seat';
import { useLocale, useT } from '../messages';
import type { Session } from '../wire';

/**
 * Retail (module `products`, spec §7.3.1).
 *
 * It sits beside the menu in Settings because that is what it is: the same
 * catalog. A product is an **item of kind `stocked`**, not a row in a second
 * table — which is why a jar of oil and a massage go onto the same checkout
 * line, through the same pricing, into the same reports. Two panels here, one
 * `items` underneath.
 *
 * Retire rather than delete, exactly as services do and for the same reason:
 * there is no `DELETE /v1/products`, because a product somebody bought last
 * March is part of the ledger. `active: false` takes it off the shelf and leaves
 * the history intact.
 *
 * ## Who may change it, and what the website shows (B10)
 *
 * Adding, editing and retiring are drawn for `canChangeCatalog` — the owner,
 * and staff when the owner allowed it — because `core-api` refuses every other
 * seat. Anybody else sees the shelf, a "Details" door to each product's
 * photographs, and one sentence saying why there is nothing else to press.
 * "Show on the website" is one more field in the editor's diff: off keeps the
 * product at the till and takes it off the shop page.
 */
export function ProductsPanel({ currency }: { currency: string }): ReactNode {
  const runtime = useRuntime();
  const actions = useActions();
  const t = useT();
  const locale = useLocale();
  const products = useLive(() => runtime.mirror.products(), []);
  const session = useLive(() => runtime.mirror.session(), runtime.mirror.sessionNow() ?? null);
  const canEdit = canChangeCatalog(session);

  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [brand, setBrand] = useState('');
  const [editing, setEditing] = useState<string | null>(null);

  const priceCents = centsFromInput(price);

  return (
    <article className="panel">
      <h2>{t('products.heading')}</h2>
      <CatalogReadOnlyNote session={session} />
      <ul className="service-list">
        {products.map((product) => (
          <li key={product.product_id} className={product.active ? '' : 'is-retired'}>
            <div>
              <div className="item-row">
                <ItemThumb images={product.images} />
                <span className="item-row__text">
                  <strong>{product.name}</strong>
                  <small>
                    {formatMoney(product.price_cents, currency, locale)}
                    {product.brand === '' ? '' : t('products.brandTail', { brand: product.brand })}
                  </small>
                </span>
              </div>
              {editing !== product.product_id ? null : canEdit ? (
                <ProductEditor product={product} onDone={() => setEditing(null)} />
              ) : (
                <ItemPhotos
                  mount="products"
                  itemId={product.product_id}
                  itemName={product.name}
                  images={product.images}
                />
              )}
            </div>
            <div className="row-actions">
              {product.on_website ? null : <OffWebsiteBadge />}
              <button
                type="button"
                className="link-button"
                onClick={() =>
                  setEditing(editing === product.product_id ? null : product.product_id)
                }
              >
                {editing === product.product_id
                  ? t('common.close')
                  : canEdit
                    ? t('common.edit')
                    : t('catalog.details')}
              </button>
              {canEdit ? (
                <button
                  type="button"
                  className="link-button"
                  onClick={() =>
                    void actions.updateProduct({
                      productId: product.product_id,
                      name: product.name,
                      changes: { active: !product.active },
                    })
                  }
                >
                  {product.active ? t('common.retire') : t('common.restore')}
                </button>
              ) : null}
            </div>
          </li>
        ))}
        {products.length === 0 ? <li className="muted">{t('products.empty')}</li> : null}
      </ul>

      {canEdit ? (
        <div className="editor editor--inline">
          <input
            aria-label={t('products.nameLabel')}
            placeholder={t('common.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            aria-label={t('products.priceLabel')}
            placeholder={t('common.price')}
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
          <input
            aria-label={t('products.brand')}
            placeholder={t('products.brand')}
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
          />
          <button
            type="button"
            className="button"
            disabled={name.trim() === '' || priceCents === null}
            onClick={() => {
              void actions.createProduct({
                name: name.trim(),
                priceCents: priceCents as number,
                ...(brand.trim() === '' ? {} : { brand: brand.trim() }),
              });
              setName('');
              setPrice('');
              setBrand('');
            }}
          >
            {t('products.add')}
          </button>
        </div>
      ) : null}
    </article>
  );
}

/**
 * **Why there is nothing to press** (B10) — one sentence, above a catalog this
 * seat may read and not change. A front-desk seat is told the owner decides and
 * where; a read-only login is told what it is. Nothing for a seat that may
 * change it, and nothing before the session is known (the honest first paint of
 * a cold open is "nothing to press", not an accusation).
 */
export function CatalogReadOnlyNote({ session }: { session: Session | null }): ReactNode {
  const t = useT();
  if (session === null || canChangeCatalog(session)) return null;
  return (
    <p className="note" role="note">
      {session.user.role === 'staff' && session.support === undefined
        ? t('catalog.readOnlyStaff')
        : t('catalog.readOnlySeat')}
    </p>
  );
}

/** "Not on the website", beside a product or a service a stranger does not see (B10). */
export function OffWebsiteBadge(): ReactNode {
  const t = useT();
  return (
    <span className="badge" title={t('catalog.offWebsiteTitle')}>
      {t('catalog.offWebsite')}
    </span>
  );
}

/** The diff, never the whole row — see `ServiceEditor` for the argument. */
function ProductEditor({ product, onDone }: { product: Product; onDone: () => void }): ReactNode {
  const actions = useActions();
  const t = useT();
  const [name, setName] = useState(product.name);
  const [price, setPrice] = useState(inputFromCents(product.price_cents));
  const [brand, setBrand] = useState(product.brand);
  const [onWebsite, setOnWebsite] = useState(product.on_website);

  return (
    <>
      <div className="editor editor--inline">
        <input
          aria-label={t('common.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          aria-label={t('common.price')}
          inputMode="decimal"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
        <input
          aria-label={t('products.brand')}
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
        />
        <label className="checkbox">
          <input
            type="checkbox"
            checked={onWebsite}
            onChange={(e) => setOnWebsite(e.target.checked)}
          />
          <span>{t('catalog.showOnWebsite')}</span>
        </label>
        <button
          type="button"
          className="button"
          onClick={() => {
            const priceCents = centsFromInput(price);
            const changes: Record<string, unknown> = {};
            if (name !== product.name) changes['name'] = name;
            if (priceCents !== null && priceCents !== product.price_cents) {
              changes['price_cents'] = priceCents;
            }
            if (brand !== product.brand) changes['brand'] = brand;
            if (onWebsite !== product.on_website) changes['on_website'] = onWebsite;
            if (Object.keys(changes).length > 0) {
              void actions.updateProduct({
                productId: product.product_id,
                name: product.name,
                changes,
              });
            }
            onDone();
          }}
        >
          {t('common.save')}
        </button>
      </div>
      {/*
      Beside the fields rather than inside Save: a photograph is written the
      moment it lands (B7, `ItemPhotos.tsx`), online, and has nothing to do with
      the outbox the fields above go through.
    */}
      <ItemPhotos
        mount="products"
        itemId={product.product_id}
        itemName={product.name}
        images={product.images}
      />
    </>
  );
}
